// Number formatting helpers — style Power BI / rapports financiers CH.
// L'idée : des chiffres lisibles partout (axes, légendes, tooltips, KPIs),
// avec séparateur de milliers suisse (espace insécable), pas de décimales parasites,
// et un mode compact (k / M) pour les axes denses.

const NBSP = '\u202F'; // espace fine insécable — séparateur officiel fr-CH

function hasDecimals(n: number): boolean {
  return Math.abs(n - Math.round(n)) > 0.05;
}

/** "1207000" → "1 207 000" ; "1207.5" → "1 207.5". Jamais de décimales parasites. */
export function formatNumber(n: number | string | null | undefined, opts?: { decimals?: number }): string {
  const v = typeof n === 'number' ? n : Number(String(n ?? '').replace(/[^\d.,-]/g, '').replace(',', '.'));
  if (!Number.isFinite(v)) return '—';
  const decimals = opts?.decimals ?? (hasDecimals(v) ? 1 : 0);
  return v.toLocaleString('fr-CH', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).replace(/\s/g, NBSP);
}

/** Compact pour axes : 1500 → "1.5 k", 1_207_000 → "1.2 M". */
export function formatCompact(n: number | null | undefined): string {
  const v = typeof n === 'number' ? n : Number(n ?? 0);
  if (!Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1).replace('.', ',')}${NBSP}M`;
  if (abs >= 10_000) return `${Math.round(v / 1_000)}${NBSP}k`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(1).replace('.', ',')}${NBSP}k`;
  return formatNumber(v);
}

/** "1200" → "CHF 1 200". Symbole devant à la suisse. */
export function formatCurrency(n: number | null | undefined, unit: string = 'CHF'): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  return `${unit}${NBSP}${formatNumber(n)}`;
}

/** 0.58 → "58 %" ; 58 → "58 %" (détecte si déjà en pourcents ou en fraction). */
export function formatPercent(n: number | null | undefined, opts?: { decimals?: number }): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const v = Number(n);
  const pct = Math.abs(v) <= 1 ? v * 100 : v;
  const decimals = opts?.decimals ?? (hasDecimals(pct) ? 1 : 0);
  return `${pct.toLocaleString('fr-CH', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${NBSP}%`;
}

/** Format intelligent selon l'unité détectée. */
export function formatWithUnit(n: number | null | undefined, unit?: string | null): string {
  if (n == null || !Number.isFinite(Number(n))) return '—';
  const u = (unit ?? '').trim();
  if (!u) return formatNumber(n);
  if (/^(chf|fr|eur|€|\$|usd)$/i.test(u)) return formatCurrency(n, u.toUpperCase().replace('€', 'EUR').replace('$', 'USD'));
  if (/^%$/.test(u)) return formatPercent(n);
  return `${formatNumber(n)}${NBSP}${u}`;
}

/** Formate pour un axe Y (compact si grand) avec unité optionnelle. */
export function formatAxis(n: number, unit?: string | null): string {
  const abs = Math.abs(n);
  const compact = abs >= 1_000 ? formatCompact(n) : formatNumber(n);
  if (unit && /^(chf|eur|€|\$|usd)$/i.test(unit.trim())) {
    return `${unit.toUpperCase()}${NBSP}${compact}`;
  }
  if (unit && unit.trim() && !/^%$/.test(unit.trim())) return `${compact}${NBSP}${unit}`;
  if (unit && unit.trim() === '%') return `${formatNumber(n)}${NBSP}%`;
  return compact;
}
